import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Card,
  CardContent,
  CardMedia,
  Grid,
  IconButton,
  Tooltip,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  TextField,
  Autocomplete,
  Tab,
  Tabs,
  Drawer,
  Stack,
  SelectChangeEvent,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Check as ApproveIcon,
  Close as RejectIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  ArrowBack as BackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  Gavel as BidsIcon
} from '@mui/icons-material';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/api';
import { BidStatus, BidWithRelations, InvoiceWithRelations, InvoiceStatus, InvoiceUpdate, Company, User, InvoiceAdminCreate, InvoiceScoreBundle, ScoreGrade } from '../../types/models';
import ScoreBadge from '../../components/invoices/ScoreBadge';
import { formatCurrency, formatDate, formatDateOnly, getDaysRemaining, getDueStatusText } from '../../utils/formatters';
import { getImageUrl } from '../../utils/imageUtils';

const AdminInvoices: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [invoices, setInvoices] = useState<InvoiceWithRelations[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Invoice detail dialog
  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceWithRelations | null>(null);
  const [openDetailDialog, setOpenDetailDialog] = useState(false);
  
  // Action confirmation dialog
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [confirmAction, setConfirmAction] = useState<'verify' | 'reject' | 'delete' | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Filtering and search
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Edit dialog
  const [openEditDialog, setOpenEditDialog] = useState<boolean>(false);
  const [openCreateDialog, setOpenCreateDialog] = useState<boolean>(false);
  const [editFormData, setEditFormData] = useState<Partial<InvoiceUpdate>>({});

  // Score section state (within edit dialog)
  const [scoreBundle, setScoreBundle] = useState<InvoiceScoreBundle | null>(null);
  const [overrideScoreInput, setOverrideScoreInput] = useState<string>('');
  const [overrideReasonInput, setOverrideReasonInput] = useState<string>('');
  const [scoreActionLoading, setScoreActionLoading] = useState<boolean>(false);
  const [scoreError, setScoreError] = useState<string | null>(null);
  const [createFormData, setCreateFormData] = useState<Partial<InvoiceAdminCreate>>({});
  const [createFrontImage, setCreateFrontImage] = useState<File | null>(null);
  const [createBackImage, setCreateBackImage] = useState<File | null>(null);
  
  // Company management
  const [companies, setCompanies] = useState<Company[]>([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [sellers, setSellers] = useState<User[]>([]);
  const [sellersLoading, setSellersLoading] = useState(false);
  const [openBidsDrawer, setOpenBidsDrawer] = useState(false);
  const [bidsLoading, setBidsLoading] = useState(false);
  const [bidsError, setBidsError] = useState<string | null>(null);
  const [invoiceBids, setInvoiceBids] = useState<BidWithRelations[]>([]);
  const [bidsInvoice, setBidsInvoice] = useState<InvoiceWithRelations | null>(null);
  const [acceptingBidId, setAcceptingBidId] = useState<number | null>(null);
  
  const fetchInvoices = async () => {
    setLoading(true);
    setError(null);
    try {
      // Build query parameters
      let queryParams = '';
      if (statusFilter && statusFilter !== 'all') {
        queryParams = `?status=${statusFilter}`;
      }
      
      const response = await apiService.get<InvoiceWithRelations[]>(`/invoices${queryParams}`);
      setInvoices(response);
    } catch (err: any) {
      console.error('Error fetching invoices:', err);
      setError(err.response?.data?.detail || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchCompanies = async () => {
    setCompaniesLoading(true);
    try {
      const response = await apiService.get<Company[]>('/companies');
      setCompanies(response);
    } catch (err: any) {
      console.error('Error fetching companies:', err);
      // Don't set error state for companies as it's not critical
    } finally {
      setCompaniesLoading(false);
    }
  };

  const fetchSellers = async () => {
    setSellersLoading(true);
    try {
      const response = await apiService.get<User[]>('/users?role=seller&limit=1000');
      setSellers(response);
    } catch (err: any) {
      console.error('Error fetching sellers:', err);
    } finally {
      setSellersLoading(false);
    }
  };
  
  useEffect(() => {
    fetchInvoices();
    fetchCompanies();
    fetchSellers();
  }, [statusFilter]); // Re-fetch when status filter changes
  
  // Handle navigation from dashboard with invoice to edit
  useEffect(() => {
    const state = location.state as { editInvoice?: InvoiceWithRelations };
    if (state?.editInvoice) {
      // Open edit dialog for the invoice passed from dashboard
      handleEditInvoice(state.editInvoice);
      // Clear the navigation state to prevent reopening on refresh
      navigate(location.pathname, { replace: true });
    }
  }, [location.state]);
  
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  const handleStatusFilterChange = (event: SelectChangeEvent) => {
    setStatusFilter(event.target.value);
    setPage(0); // Reset to first page when filter changes
  };
  
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };
  
  const filteredInvoices = invoices.filter(invoice => {
    // Apply search filter if search term exists
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return (
        invoice.invoice_number.toLowerCase().includes(searchLower) ||
        (invoice.seller?.full_name?.toLowerCase().includes(searchLower)) ||
        (invoice.debtor?.name?.toLowerCase().includes(searchLower))
      );
    }
    return true;
  });
  
  const handleViewInvoice = (invoice: InvoiceWithRelations) => {
    setSelectedInvoice(invoice);
    setOpenDetailDialog(true);
  };

  const fetchInvoiceBids = async (invoiceId: number) => {
    setBidsLoading(true);
    setBidsError(null);
    try {
      const response = await apiService.get<BidWithRelations[]>(`/bids?invoice_id=${invoiceId}`);
      setInvoiceBids(response);
    } catch (err: any) {
      console.error('Error fetching bids:', err);
      setBidsError(err.response?.data?.detail || 'Error al cargar las ofertas de esta factura');
    } finally {
      setBidsLoading(false);
    }
  };

  const handleOpenBidsDrawer = (invoice: InvoiceWithRelations) => {
    setBidsInvoice(invoice);
    setOpenBidsDrawer(true);
    fetchInvoiceBids(invoice.id);
  };

  const handleCloseBidsDrawer = () => {
    setOpenBidsDrawer(false);
    setInvoiceBids([]);
    setBidsInvoice(null);
    setBidsError(null);
  };

  const handleAdminAcceptBid = async (bidId: number) => {
    setAcceptingBidId(bidId);
    setBidsError(null);
    try {
      await apiService.put(`/bids/admin/${bidId}/accept`);
      // Refresh bids list
      if (bidsInvoice) {
        fetchInvoiceBids(bidsInvoice.id);
      }
      // Refresh invoices list since status might have changed
      fetchInvoices();
    } catch (err: any) {
      console.error('Error accepting bid:', err);
      setBidsError(err.response?.data?.detail || 'Error al aceptar la oferta');
    } finally {
      setAcceptingBidId(null);
    }
  };
  
  const handleCloseDetailDialog = () => {
    setOpenDetailDialog(false);
  };
  
  const handleOpenConfirmDialog = (invoice: InvoiceWithRelations, action: 'verify' | 'reject' | 'delete') => {
    setSelectedInvoice(invoice);
    setConfirmAction(action);
    setOpenConfirmDialog(true);
  };
  
  const handleEditInvoice = (invoice: InvoiceWithRelations) => {
    setSelectedInvoice(invoice);
    // Initialize edit form data with current invoice values
    setEditFormData({
      debtor_id: invoice.debtor_id || undefined,  // Include debtor company ID
      issuer_id: invoice.issuer_id || undefined,
      invoice_number: invoice.invoice_number,
      timbrado: invoice.timbrado,
      issue_date: invoice.issue_date,
      due_date: invoice.due_date,
      amount: invoice.amount,
      tax_retention: invoice.tax_retention,
      tax_amount: invoice.tax_amount,
      currency: invoice.currency,
      description: invoice.description,
      minimum_amount: invoice.minimum_amount,
      score_visible: invoice.score_visible ?? false,
      // Handle status properly for TypeScript
      status: typeof invoice.status === 'string' ?
        (invoice.status as unknown as InvoiceStatus) :
        invoice.status
    });
    // Load score bundle and reset override inputs
    setScoreBundle(null);
    setOverrideScoreInput('');
    setOverrideReasonInput('');
    setScoreError(null);
    apiService
      .get<InvoiceScoreBundle>(`/invoices/${invoice.id}/score`)
      .then((data) => setScoreBundle(data))
      .catch(() => setScoreBundle(null));
    setOpenEditDialog(true);
  };

  const handleRecomputeScore = async () => {
    if (!selectedInvoice) return;
    setScoreActionLoading(true);
    setScoreError(null);
    try {
      const data = await apiService.post<InvoiceScoreBundle>(
        `/invoices/${selectedInvoice.id}/score/recompute`,
        {}
      );
      setScoreBundle(data);
    } catch (err: any) {
      setScoreError(err.response?.data?.detail || 'No se pudo calcular el puntaje');
    } finally {
      setScoreActionLoading(false);
    }
  };

  const handleSaveScoreOverride = async () => {
    if (!selectedInvoice) return;
    const scoreValue = parseFloat(overrideScoreInput);
    if (Number.isNaN(scoreValue) || scoreValue < 0 || scoreValue > 100) {
      setScoreError('El puntaje debe ser un número entre 0 y 100');
      return;
    }
    if (!overrideReasonInput.trim()) {
      setScoreError('La razón es obligatoria');
      return;
    }
    setScoreActionLoading(true);
    setScoreError(null);
    try {
      const data = await apiService.post<InvoiceScoreBundle>(
        `/invoices/${selectedInvoice.id}/score/override`,
        { score: scoreValue, reason: overrideReasonInput.trim() }
      );
      setScoreBundle(data);
      setOverrideScoreInput('');
      setOverrideReasonInput('');
    } catch (err: any) {
      setScoreError(err.response?.data?.detail || 'No se pudo guardar el override');
    } finally {
      setScoreActionLoading(false);
    }
  };

  const handleClearScoreOverride = async () => {
    if (!selectedInvoice) return;
    setScoreActionLoading(true);
    setScoreError(null);
    try {
      const data = await apiService.delete<InvoiceScoreBundle>(
        `/invoices/${selectedInvoice.id}/score/override`
      );
      setScoreBundle(data);
    } catch (err: any) {
      setScoreError(err.response?.data?.detail || 'No se pudo quitar el override');
    } finally {
      setScoreActionLoading(false);
    }
  };
  
  const handleCloseConfirmDialog = () => {
    setOpenConfirmDialog(false);
    setConfirmAction(null);
  };
  
  const handleConfirmAction = async () => {
    if (!selectedInvoice || !confirmAction) return;
    
    setActionLoading(true);
    
    try {
      if (confirmAction === 'verify') {
        await apiService.put(`/invoices/${selectedInvoice.id}`, {
          status: InvoiceStatus.VERIFIED
        });
        setActionSuccess(`Invoice #${selectedInvoice.invoice_number} has been verified successfully`);
      } else if (confirmAction === 'reject') {
        await apiService.put(`/invoices/${selectedInvoice.id}`, {
          status: InvoiceStatus.REJECTED
        });
        setActionSuccess(`Invoice #${selectedInvoice.invoice_number} has been rejected`);
      } else if (confirmAction === 'delete') {
        // Since there's no DELETE endpoint, we'll use a workaround by setting status to DELISTED
        await apiService.put(`/invoices/${selectedInvoice.id}`, {
          status: InvoiceStatus.DELISTED
        });
        setActionSuccess(`Invoice #${selectedInvoice.invoice_number} has been deleted`);
      }
      
      // Refresh invoices list
      fetchInvoices();
    } catch (err: any) {
      console.error(`Error ${confirmAction}ing invoice:`, err);
      setError(err.response?.data?.detail || `Failed to ${confirmAction} invoice`);
    } finally {
      setActionLoading(false);
      handleCloseConfirmDialog();
    }
  };
  
  const handleCloseCreateDialog = () => {
    setOpenCreateDialog(false);
    setCreateFrontImage(null);
    setCreateBackImage(null);
  };

  const handleCreateInvoice = async () => {
    setActionLoading(true);
    setError(null);

    if (!createFormData.seller_id) {
      setError('Seleccione un vendedor para la factura');
      setActionLoading(false);
      return;
    }

    if (!createFrontImage) {
      setError('La imagen frontal de la factura es obligatoria');
      setActionLoading(false);
      return;
    }

    try {
      const payload: InvoiceAdminCreate = {
        ...(createFormData as InvoiceAdminCreate),
        seller_id: Number(createFormData.seller_id),
        status: (createFormData.status as InvoiceStatus) || InvoiceStatus.PENDING
      };

      const multipartData = new FormData();
      multipartData.append('invoice_data', JSON.stringify(payload));
      multipartData.append('front_image', createFrontImage);
      if (createBackImage) {
        multipartData.append('back_image', createBackImage);
      }

      await apiService.uploadFile('/invoices/admin', multipartData);
      setActionSuccess('Invoice created successfully');
      handleCloseCreateDialog();
      fetchInvoices(); // Refresh the list
    } catch (err: any) {
      console.error('Error creating invoice:', err);
      setError(err.response?.data?.detail || 'Failed to create invoice');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveInvoice = async () => {
    if (!selectedInvoice || !editFormData) return;
    
    setActionLoading(true);
    try {
      // Calculate net_amount based on amount, tax_amount, and tax_retention
      const netAmount = editFormData.amount !== undefined && 
                       editFormData.tax_amount !== undefined && 
                       editFormData.tax_retention !== undefined ? 
                       Number(editFormData.amount) - (Number(editFormData.tax_amount) * Number(editFormData.tax_retention) / 100) : 
                       undefined;
      
      // Ensure status is properly typed for the API call
      const formDataToSend = {
        ...editFormData,
        net_amount: netAmount,
        status: typeof editFormData.status === 'string' ?
          (editFormData.status as unknown as InvoiceStatus) :
          editFormData.status
      };

      await apiService.put(`/invoices/${selectedInvoice.id}`, formDataToSend);

      setActionSuccess(`Invoice #${selectedInvoice.invoice_number} has been updated successfully`);

      // Refresh invoices list
      fetchInvoices();
      setOpenEditDialog(false);
    } catch (err: any) {
      console.error('Error updating invoice:', err);
      setError(err.response?.data?.detail || 'Failed to update invoice');
    } finally {
      setActionLoading(false);
    }
  };
  
  const getStatusChip = (status: InvoiceStatus | string) => {
    switch (status) {
      case InvoiceStatus.PENDING:
      case 'pending':
        return <Chip label="Pending" color="warning" size="small" />;
      case InvoiceStatus.VERIFIED:
      case 'verified':
        return <Chip label="Verified" color="success" size="small" />;
      case InvoiceStatus.SOLD:
      case 'sold':
        return <Chip label="Sold" color="info" size="small" />;
      case InvoiceStatus.EXPIRED:
      case 'expired':
        return <Chip label="Expired" color="error" size="small" />;
      case InvoiceStatus.REJECTED:
      case 'rejected':
        return <Chip label="Rejected" color="error" size="small" />;
      case InvoiceStatus.DELISTED:
      case 'delisted':
        return <Chip label="Delisted" color="default" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  const getBidStatusChip = (status: BidStatus | string) => {
    switch (status) {
      case BidStatus.ACTIVE:
      case 'active':
        return <Chip label="Activa" color="primary" size="small" />;
      case BidStatus.ACCEPTED:
      case 'accepted':
        return <Chip label="Aceptada" color="success" size="small" />;
      case BidStatus.REJECTED:
      case 'rejected':
        return <Chip label="Rechazada" color="error" size="small" />;
      case BidStatus.EXPIRED:
      case 'expired':
        return <Chip label="Vencida" color="warning" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };
  
  const handleNewInvoice = () => {
    // Initialize empty form data for new invoice
    setCreateFormData({
      invoice_number: '',
      timbrado: '',
      issue_date: new Date().toISOString().split('T')[0],
      due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // Default to 30 days from now
      amount: 0,
      tax_amount: 0,
      currency: 'USD',
      description: '',
      seller_id: undefined,
      debtor_id: undefined,
      issuer_id: undefined,
      minimum_amount: undefined,
      status: InvoiceStatus.PENDING
    });
    setCreateFrontImage(null);
    setCreateBackImage(null);
    setOpenCreateDialog(true);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link component={RouterLink} to="/dashboard" underline="hover" color="inherit">
          Panel de Control
        </Link>
        <Typography color="text.primary">Gestionar Facturas</Typography>
      </Breadcrumbs>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom component="div">
          Gestionar Facturas
        </Typography>
        <Box>
          <Button 
            variant="outlined" 
            startIcon={<AddIcon />}
            onClick={handleNewInvoice}
            sx={{ mr: 1 }}
          >
            Nueva Factura
          </Button>
          <Button 
            variant="outlined" 
            startIcon={<RefreshIcon />}
            onClick={fetchInvoices}
          >
            Actualizar
          </Button>
        </Box>
      </Box>
      
      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">Filtros</Typography>
          <Button 
            startIcon={<FilterIcon />} 
            onClick={() => setShowFilters(!showFilters)}
            size="small"
          >
            {showFilters ? 'Ocultar Filtros' : 'Mostrar Filtros'}
          </Button>
        </Box>
        
        {showFilters && (
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="status-filter-label">Estado</InputLabel>
                <Select
                  labelId="status-filter-label"
                  value={statusFilter}
                  label="Estado"
                  onChange={handleStatusFilterChange}
                >
                  <MenuItem value="all">Todos los Estados</MenuItem>
                  <MenuItem value={InvoiceStatus.PENDING}>Pendiente</MenuItem>
                  <MenuItem value={InvoiceStatus.VERIFIED}>Verificada</MenuItem>
                  <MenuItem value={InvoiceStatus.SOLD}>Vendida</MenuItem>
                  <MenuItem value={InvoiceStatus.EXPIRED}>Vencida</MenuItem>
                  <MenuItem value={InvoiceStatus.REJECTED}>Rechazada</MenuItem>
                  <MenuItem value={InvoiceStatus.DELISTED}>Retirada</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Buscar"
                variant="outlined"
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder="Buscar por factura #, vendedor, deudor..."
                InputProps={{
                  startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>
          </Grid>
        )}
      </Paper>
      
      {actionSuccess && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setActionSuccess(null)}>
          {actionSuccess}
        </Alert>
      )}
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : filteredInvoices.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6">No se encontraron facturas</Typography>
          <Typography variant="body2" color="text.secondary">
            Intenta cambiar tus filtros o criterios de búsqueda
          </Typography>
        </Paper>
      ) : (
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer>
            <Table stickyHeader aria-label="invoices table">
              <TableHead>
                <TableRow>
                  <TableCell>Factura #</TableCell>
                  <TableCell>Vendedor</TableCell>
                  <TableCell>Deudor</TableCell>
                  <TableCell>Fecha de Emisión</TableCell>
                  <TableCell>Fecha de Vencimiento</TableCell>
                  <TableCell align="right">Monto</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredInvoices
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((invoice) => {
                    const daysRemaining = getDaysRemaining(invoice.due_date);
                    
                    return (
                      <TableRow key={invoice.id} hover>
                        <TableCell>{invoice.invoice_number}</TableCell>
                        <TableCell>
                          {invoice.seller ? invoice.seller.full_name : 'Unknown Seller'}
                        </TableCell>
                        <TableCell>
                          {invoice.debtor ? invoice.debtor.name : 'Unknown Debtor'}
                        </TableCell>
                        <TableCell>{formatDateOnly(invoice.issue_date)}</TableCell>
                        <TableCell>
                          <Box>
                            {formatDateOnly(invoice.due_date)}
                            <Typography variant="caption" display="block" color={getDueStatusText(daysRemaining).color}>
                              {getDueStatusText(daysRemaining).text}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(invoice.amount, invoice.currency)}
                        </TableCell>
                        <TableCell>{getStatusChip(invoice.status)}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex' }}>
                            <Tooltip title="Ver Detalles">
                              <IconButton
                                size="small"
                                onClick={() => handleViewInvoice(invoice)}
                                color="primary"
                              >
                                <ViewIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Editar Factura">
                              <IconButton
                                size="small"
                                onClick={() => handleEditInvoice(invoice)}
                                color="primary"
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Eliminar Factura">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenConfirmDialog(invoice, 'delete')}
                                color="error"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Ver Ofertas">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenBidsDrawer(invoice)}
                                color="primary"
                              >
                                <BidsIcon />
                              </IconButton>
                            </Tooltip>
                            
                            {/* Status-specific actions */}
                            {invoice.status === InvoiceStatus.PENDING && invoice.debtor_id && (
                              <>
                                <Tooltip title="Verificar Factura">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleOpenConfirmDialog(invoice, 'verify')}
                                    color="success"
                                  >
                                    <ApproveIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Rechazar Factura">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleOpenConfirmDialog(invoice, 'reject')}
                                    color="error"
                                  >
                                    <RejectIcon />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={filteredInvoices.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      )}
      
      {/* Invoice Detail Dialog */}
      <Dialog
        open={openDetailDialog}
        onClose={handleCloseDetailDialog}
        fullWidth
        maxWidth="md"
      >
        {selectedInvoice && (
          <>
            <DialogTitle>
              Factura #{selectedInvoice.invoice_number}
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardMedia
                      component="img"
                      height="300"
                      image={getImageUrl(selectedInvoice.front_image_path)}
                      alt="Frente de la Factura"
                      sx={{ objectFit: 'contain', bgcolor: 'background.default' }}
                    />
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary">
                        Imagen Frontal
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                {selectedInvoice.back_image_path && (
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardMedia
                        component="img"
                        height="300"
                        image={getImageUrl(selectedInvoice.back_image_path)}
                        alt="Reverso de la Factura"
                        sx={{ objectFit: 'contain', bgcolor: 'background.default' }}
                      />
                      <CardContent>
                        <Typography variant="subtitle2" color="text.secondary">
                          Imagen Trasera
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
                
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Detalles de la Factura
                  </Typography>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Número de Factura
                      </Typography>
                      <Typography variant="body1">
                        {selectedInvoice.invoice_number}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Fecha de Emisión
                      </Typography>
                      <Typography variant="body1">
                        {formatDateOnly(selectedInvoice.issue_date)}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Fecha de Vencimiento
                      </Typography>
                      <Typography variant="body1">
                        {formatDateOnly(selectedInvoice.due_date)}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Monto
                      </Typography>
                      <Typography variant="body1">
                        {formatCurrency(selectedInvoice.amount, selectedInvoice.currency)}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Monto de Impuestos
                      </Typography>
                      <Typography variant="body1">
                        {formatCurrency(selectedInvoice.tax_amount, selectedInvoice.currency)}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Retención (%)
                      </Typography>
                      <Typography variant="body1">
                        {selectedInvoice.tax_retention || 0}%
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Monto Neto
                      </Typography>
                      <Typography variant="body1">
                        {formatCurrency(selectedInvoice.net_amount, selectedInvoice.currency)}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Estado
                      </Typography>
                      <Typography variant="body1">
                        {getStatusChip(selectedInvoice.status)}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Descripción
                      </Typography>
                      <Typography variant="body1">
                        {selectedInvoice.description || 'No se proporcionó descripción'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDetailDialog}>Cerrar</Button>
              <Button 
                color="primary" 
                variant="contained" 
                onClick={() => {
                  handleCloseDetailDialog();
                  handleEditInvoice(selectedInvoice);
                }}
              >
                Editar Factura
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
      
      {/* Confirmation Dialog */}
      <Dialog
        open={openConfirmDialog}
        onClose={handleCloseConfirmDialog}
        aria-labelledby="confirm-dialog-title"
      >
        <DialogTitle id="confirm-dialog-title">
          {confirmAction === 'verify' ? 'Verificar Factura' : 
           confirmAction === 'reject' ? 'Rechazar Factura' : 
           'Eliminar Factura'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmAction === 'verify' 
              ? `¿Estás seguro de que deseas verificar la factura #${selectedInvoice?.invoice_number}? Esto la hará disponible en el mercado.`
              : confirmAction === 'reject'
              ? `¿Estás seguro de que deseas rechazar la factura #${selectedInvoice?.invoice_number}? Esta acción no se puede deshacer.`
              : `¿Estás seguro de que deseas eliminar la factura #${selectedInvoice?.invoice_number}? Esta acción no se puede deshacer.`
            }
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog} disabled={actionLoading}>
            Cancelar
          </Button>
          <Button 
            onClick={handleConfirmAction} 
            color={confirmAction === 'verify' ? 'success' : 'error'}
            variant="contained"
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={20} /> : null}
          >
            {actionLoading 
              ? 'Procesando...'
              : confirmAction === 'verify' ? 'Verificar' : 
                confirmAction === 'reject' ? 'Rechazar' : 'Eliminar'
            }
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Edit Invoice Dialog */}
      <Dialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        fullWidth
        maxWidth="md"
      >
        {selectedInvoice && (
          <>
            <DialogTitle>
              Editar Factura #{selectedInvoice.invoice_number}
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Número de Factura"
                    value={editFormData.invoice_number || ''}
                    onChange={(e) => setEditFormData({...editFormData, invoice_number: e.target.value})}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel id="status-select-label">Estado</InputLabel>
                    <Select
                      labelId="status-select-label"
                      value={editFormData.status || ''}
                      label="Estado"
                      onChange={(e) => setEditFormData({...editFormData, status: e.target.value as unknown as InvoiceStatus})}
                    >
                      <MenuItem value={InvoiceStatus.PENDING}>Pendiente</MenuItem>
                      <MenuItem value={InvoiceStatus.VERIFIED}>Verificada</MenuItem>
                      <MenuItem value={InvoiceStatus.SOLD}>Vendida</MenuItem>
                      <MenuItem value={InvoiceStatus.EXPIRED}>Vencida</MenuItem>
                      <MenuItem value={InvoiceStatus.REJECTED}>Rechazada</MenuItem>
                      <MenuItem value={InvoiceStatus.DELISTED}>Retirada</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={companies}
                    loading={companiesLoading}
                    value={companies.find(c => c.id === (editFormData.debtor_id as number)) || null}
                    onChange={(event, value) => setEditFormData({ ...editFormData, debtor_id: value ? value.id : undefined })}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    getOptionLabel={(option) => `${option.name} (${option.tax_id})`}
                    renderInput={(params) => (
                      <TextField {...params} label="Empresa Deudora" margin="normal" placeholder="No se ha asignado empresa" />
                    )}
                    clearOnEscape
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={companies}
                    loading={companiesLoading}
                    value={companies.find(c => c.id === (editFormData.issuer_id as number)) || null}
                    onChange={(event, value) => setEditFormData({ ...editFormData, issuer_id: value ? value.id : undefined })}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    getOptionLabel={(option) => `${option.name} (${option.tax_id})`}
                    renderInput={(params) => (
                      <TextField {...params} label="Empresa Emisora" margin="normal" placeholder="No se ha asignado empresa" />
                    )}
                    clearOnEscape
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Fecha de Emisión"
                    type="date"
                    value={editFormData.issue_date?.split('T')[0] || ''}
                    onChange={(e) => setEditFormData({...editFormData, issue_date: e.target.value})}
                    margin="normal"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Fecha de Vencimiento"
                    type="date"
                    value={editFormData.due_date?.split('T')[0] || ''}
                    onChange={(e) => setEditFormData({...editFormData, due_date: e.target.value})}
                    margin="normal"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Monto"
                    type="number"
                    value={editFormData.amount || ''}
                    onChange={(e) => setEditFormData({...editFormData, amount: parseFloat(e.target.value)})}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Moneda"
                    value={editFormData.currency || ''}
                    onChange={(e) => setEditFormData({...editFormData, currency: e.target.value})}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Monto de Impuestos"
                    type="number"
                    value={editFormData.tax_amount || ''}
                    onChange={(e) => setEditFormData({...editFormData, tax_amount: parseFloat(e.target.value)})}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel id="tax-retention-select-label">Retención (%)</InputLabel>
                    <Select
                      labelId="tax-retention-select-label"
                      value={editFormData.tax_retention !== undefined ? editFormData.tax_retention : 0}
                      label="Retención (%)"
                      onChange={(e) => setEditFormData({...editFormData, tax_retention: Number(e.target.value)})}
                    >
                      <MenuItem value={0}>0%</MenuItem>
                      <MenuItem value={30}>30%</MenuItem>
                      <MenuItem value={70}>70%</MenuItem>
                    </Select>
                    <FormHelperText>Porcentaje de retención de impuestos</FormHelperText>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Monto Neto"
                    type="number"
                    value={editFormData.amount !== undefined && editFormData.tax_amount !== undefined && editFormData.tax_retention !== undefined ? 
                      (Number(editFormData.amount) - (Number(editFormData.tax_amount) * Number(editFormData.tax_retention) / 100)).toFixed(2) : ''}
                    margin="normal"
                    disabled
                    helperText="Calculado automáticamente (Monto - (Impuestos * Retención/100))"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Timbrado"
                    value={editFormData.timbrado || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, timbrado: e.target.value })}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Monto Mínimo"
                    type="number"
                    value={editFormData.minimum_amount ?? ''}
                    onChange={(e) => setEditFormData({ ...editFormData, minimum_amount: parseFloat(e.target.value) })}
                    margin="normal"
                    helperText="Monto mínimo requerido para ofertas"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      Calificación de Riesgo
                    </Typography>

                    {scoreError && (
                      <Alert severity="error" sx={{ mb: 2 }} onClose={() => setScoreError(null)}>
                        {scoreError}
                      </Alert>
                    )}

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" color="text.secondary">Efectivo:</Typography>
                        {scoreBundle?.effective_grade ? (
                          <ScoreBadge
                            grade={scoreBundle.effective_grade as ScoreGrade}
                            score={scoreBundle.effective_score ?? undefined}
                            size="small"
                            overridden={scoreBundle.source === 'override'}
                          />
                        ) : (
                          <Typography variant="caption" color="text.disabled">sin calcular</Typography>
                        )}
                      </Box>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={handleRecomputeScore}
                        disabled={scoreActionLoading}
                        startIcon={scoreActionLoading ? <CircularProgress size={14} /> : <RefreshIcon />}
                      >
                        Recalcular
                      </Button>
                    </Box>

                    {scoreBundle?.auto && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        Auto: {scoreBundle.auto.score.toFixed(1)} · {scoreBundle.auto.grade}
                        {' · '}confianza {scoreBundle.auto.confidence}
                      </Typography>
                    )}

                    {scoreBundle?.override && (
                      <Box sx={{ mb: 2, p: 1.5, bgcolor: 'info.50', borderRadius: 1, border: '1px solid', borderColor: 'info.light' }}>
                        <Typography variant="body2" fontWeight={600}>
                          Override activo: {scoreBundle.override.score.toFixed(1)} · {scoreBundle.override.grade}
                        </Typography>
                        {scoreBundle.override.reason && (
                          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            "{scoreBundle.override.reason}"
                          </Typography>
                        )}
                        <Box sx={{ mt: 1 }}>
                          <Button
                            size="small"
                            color="warning"
                            onClick={handleClearScoreOverride}
                            disabled={scoreActionLoading}
                          >
                            Quitar override
                          </Button>
                        </Box>
                      </Box>
                    )}

                    <Divider sx={{ my: 2 }} />
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                      {scoreBundle?.override ? 'Actualizar override' : 'Establecer override manual'}
                    </Typography>
                    <Grid container spacing={2} alignItems="flex-start">
                      <Grid item xs={12} sm={3}>
                        <TextField
                          fullWidth
                          label="Puntaje (0–100)"
                          type="number"
                          value={overrideScoreInput}
                          onChange={(e) => setOverrideScoreInput(e.target.value)}
                          inputProps={{ min: 0, max: 100, step: 0.1 }}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Razón (obligatoria)"
                          value={overrideReasonInput}
                          onChange={(e) => setOverrideReasonInput(e.target.value)}
                          multiline
                          minRows={1}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <Button
                          fullWidth
                          variant="contained"
                          color="primary"
                          onClick={handleSaveScoreOverride}
                          disabled={scoreActionLoading || !overrideScoreInput || !overrideReasonInput.trim()}
                        >
                          Guardar override
                        </Button>
                      </Grid>
                    </Grid>
                    <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1 }}>
                      El grado se deriva automáticamente del puntaje. El override sobrevive a futuros recálculos.
                    </Typography>

                    <Divider sx={{ my: 2 }} />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={editFormData.score_visible ?? false}
                          onChange={(e) => setEditFormData({ ...editFormData, score_visible: e.target.checked })}
                          color="primary"
                        />
                      }
                      label="Publicar rating a compradores"
                    />
                    <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                      Mientras esté desactivado, los compradores no verán el rating de esta factura.
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Descripción"
                    multiline
                    rows={4}
                    value={editFormData.description || ''}
                    onChange={(e) => setEditFormData({...editFormData, description: e.target.value})}
                    margin="normal"
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenEditDialog(false)}>Cancelar</Button>
              <Button 
                color="primary" 
                variant="contained" 
                onClick={handleSaveInvoice}
              >
                Guardar Cambios
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Create Invoice Dialog */}
      <Dialog
        open={openCreateDialog}
        onClose={handleCloseCreateDialog}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          Crear Nueva Factura
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Número de Factura"
                value={createFormData.invoice_number || ''}
                onChange={(e) => setCreateFormData({...createFormData, invoice_number: e.target.value})}
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={sellers}
                loading={sellersLoading}
                value={sellers.find(s => s.id === Number(createFormData.seller_id)) || null}
                onChange={(event, value) => setCreateFormData({ ...createFormData, seller_id: value ? value.id : undefined })}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionLabel={(option) => `${option.full_name} (${option.email})`}
                renderInput={(params) => (
                  <TextField {...params} label="Vendedor" margin="normal" required />
                )}
                clearOnEscape
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel id="create-status-select-label">Estado</InputLabel>
                <Select
                  labelId="create-status-select-label"
                  value={createFormData.status || InvoiceStatus.PENDING}
                  label="Estado"
                  onChange={(e) => setCreateFormData({...createFormData, status: e.target.value as unknown as InvoiceStatus})}
                >
                  <MenuItem value={InvoiceStatus.PENDING}>Pendiente</MenuItem>
                  <MenuItem value={InvoiceStatus.VERIFIED}>Verificada</MenuItem>
                  <MenuItem value={InvoiceStatus.SOLD}>Vendida</MenuItem>
                  <MenuItem value={InvoiceStatus.EXPIRED}>Vencida</MenuItem>
                  <MenuItem value={InvoiceStatus.REJECTED}>Rechazada</MenuItem>
                  <MenuItem value={InvoiceStatus.DELISTED}>Retirada</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={companies}
                loading={companiesLoading}
                value={companies.find(c => c.id === (createFormData.debtor_id as number)) || null}
                onChange={(event, value) => setCreateFormData({ ...createFormData, debtor_id: value ? value.id : undefined })}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionLabel={(option) => `${option.name} (${option.tax_id})`}
                renderInput={(params) => (
                  <TextField {...params} label="Empresa Deudora" margin="normal" placeholder="No se ha asignado empresa" required />
                )}
                clearOnEscape
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={companies}
                loading={companiesLoading}
                value={companies.find(c => c.id === (createFormData.issuer_id as number)) || null}
                onChange={(event, value) => setCreateFormData({ ...createFormData, issuer_id: value ? value.id : undefined })}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionLabel={(option) => `${option.name} (${option.tax_id})`}
                renderInput={(params) => (
                  <TextField {...params} label="Empresa Emisora" margin="normal" placeholder="No se ha asignado empresa" />
                )}
                clearOnEscape
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Fecha de Emisión"
                type="date"
                value={createFormData.issue_date || ''}
                onChange={(e) => setCreateFormData({...createFormData, issue_date: e.target.value})}
                margin="normal"
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Fecha de Vencimiento"
                type="date"
                value={createFormData.due_date || ''}
                onChange={(e) => setCreateFormData({...createFormData, due_date: e.target.value})}
                margin="normal"
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Monto"
                type="number"
                value={createFormData.amount || ''}
                onChange={(e) => setCreateFormData({...createFormData, amount: parseFloat(e.target.value)})}
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Moneda"
                value={createFormData.currency || ''}
                onChange={(e) => setCreateFormData({...createFormData, currency: e.target.value})}
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Imagen Frontal"
                type="file"
                margin="normal"
                InputLabelProps={{ shrink: true }}
                inputProps={{ accept: 'image/*' }}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCreateFrontImage(e.target.files?.[0] || null)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Imagen Trasera (Opcional)"
                type="file"
                margin="normal"
                InputLabelProps={{ shrink: true }}
                inputProps={{ accept: 'image/*' }}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCreateBackImage(e.target.files?.[0] || null)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Monto de Impuestos"
                type="number"
                value={createFormData.tax_amount || ''}
                onChange={(e) => setCreateFormData({...createFormData, tax_amount: parseFloat(e.target.value)})}
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel id="create-tax-retention-select-label">Retención (%)</InputLabel>
                <Select
                  labelId="create-tax-retention-select-label"
                  value={createFormData.tax_retention !== undefined ? createFormData.tax_retention : 0}
                  label="Retención (%)"
                  onChange={(e) => setCreateFormData({...createFormData, tax_retention: Number(e.target.value)})}
                >
                  <MenuItem value={0}>0%</MenuItem>
                  <MenuItem value={30}>30%</MenuItem>
                  <MenuItem value={70}>70%</MenuItem>
                </Select>
                <FormHelperText>Porcentaje de retención de impuestos</FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Timbrado"
                value={createFormData.timbrado || ''}
                onChange={(e) => setCreateFormData({ ...createFormData, timbrado: e.target.value })}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Monto Neto"
                type="number"
                value={createFormData.amount !== undefined && createFormData.tax_amount !== undefined && createFormData.tax_retention !== undefined ? 
                  (Number(createFormData.amount) - (Number(createFormData.tax_amount) * Number(createFormData.tax_retention) / 100)).toFixed(2) : ''}
                margin="normal"
                disabled
                helperText="Calculado automáticamente (Monto - (Impuestos * Retención/100))"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Monto Mínimo"
                type="number"
                value={createFormData.minimum_amount ?? ''}
                onChange={(e) => setCreateFormData({ ...createFormData, minimum_amount: parseFloat(e.target.value) })}
                margin="normal"
                helperText="Monto mínimo requerido para ofertas"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descripción"
                multiline
                rows={4}
                value={createFormData.description || ''}
                onChange={(e) => setCreateFormData({...createFormData, description: e.target.value})}
                margin="normal"
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateDialog}>Cancelar</Button>
          <Button 
            color="primary" 
            variant="contained" 
            onClick={handleCreateInvoice}
          >
            Crear Factura
          </Button>
        </DialogActions>
      </Dialog>

      <Drawer anchor="right" open={openBidsDrawer} onClose={handleCloseBidsDrawer}>
        <Box sx={{ width: { xs: '100vw', sm: 600, md: 720 }, p: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Box>
              <Typography variant="h6">Ofertas de la Factura</Typography>
              {bidsInvoice && (
                <Typography variant="subtitle2" color="text.secondary">
                  #{bidsInvoice.invoice_number} · {formatCurrency(bidsInvoice.net_amount, bidsInvoice.currency)}
                </Typography>
              )}
            </Box>
            <Button variant="text" onClick={handleCloseBidsDrawer}>
              Cerrar
            </Button>
          </Stack>

          {bidsError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setBidsError(null)}>
              {bidsError}
            </Alert>
          )}

          {bidsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : invoiceBids.length === 0 ? (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body1">Aún no hay ofertas para esta factura.</Typography>
              <Typography variant="body2" color="text.secondary">
                Invita a compradores a ofertar para verla aquí.
              </Typography>
            </Paper>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small" aria-label="invoice bids table">
                <TableHead>
                  <TableRow>
                    <TableCell>Comprador</TableCell>
                    <TableCell align="right">Monto Ofertado</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoiceBids.map((bid) => (
                    <TableRow key={bid.id} hover>
                      <TableCell>{bid.buyer?.full_name || 'Comprador desconocido'}</TableCell>
                      <TableCell align="right">
                        {formatCurrency(Number(bid.amount), bidsInvoice?.currency || 'PYG')}
                      </TableCell>
                      <TableCell>{getBidStatusChip(bid.status)}</TableCell>
                      <TableCell>{formatDate(bid.created_at)}</TableCell>
                      <TableCell>
                        {bid.status === BidStatus.ACTIVE && (
                          <Tooltip title="Aceptar Oferta">
                            <IconButton
                              size="small"
                              onClick={() => handleAdminAcceptBid(bid.id)}
                              color="success"
                              disabled={acceptingBidId === bid.id}
                            >
                              {acceptingBidId === bid.id ? <CircularProgress size={20} /> : <ApproveIcon />}
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Drawer>
    </Container>
  );
};

export default AdminInvoices;
